import type { ResolvedPageContext } from '../core/engine/contracts'
import { PagePermaLink } from '@/const'
import { DataClassName } from '@/Data/const'

const PERMALINK_TO_DATA_CLASS: Partial<Record<PagePermaLink, DataClassName>> = {
  [PagePermaLink.ACTIVITIES]: DataClassName.ACTIVITY,
  [PagePermaLink.COMPANIES]: DataClassName.COMPANY,
  [PagePermaLink.DOCUMENTS]: DataClassName.DOCUMENT,
  [PagePermaLink.LEADS]: DataClassName.LEAD,
  [PagePermaLink.LISTS]: DataClassName.LIST,
  [PagePermaLink.MY_WORK]: DataClassName.ACTIVITY,
  [PagePermaLink.MY_CONTACTS]: DataClassName.CONTACT,
  [PagePermaLink.OPPORTUNITIES]: DataClassName.OPPORTUNITY,
  [PagePermaLink.ORDERS]: DataClassName.ORDER,
  [PagePermaLink.PERSONS]: DataClassName.PERSON,
  [PagePermaLink.QUOTES]: DataClassName.QUOTE,
  [PagePermaLink.TICKETS]: DataClassName.TICKET,
  [PagePermaLink.TIME_TRACKING]: DataClassName.TIMERECORD,
}

/**
 * Resolve the list/overview DataClass from the pathname. The URL slug matches
 * the page's permalink for every overview page, so it's matched against the
 * enum map.
 */
function resolveListDataClass(pathname: string): DataClassName | null {
  const matches = (perma: string): boolean => pathname.includes(`/${perma}`)

  for (const [perma, dataClass] of Object.entries(PERMALINK_TO_DATA_CLASS)) {
    if (matches(perma)) {
      return dataClass ?? null
    }
  }
  return null
}

// Detail page paths follow the pattern `/<entity>-details?<entity>_id=<id>`.
// These are Scrivito CMS page permalinks not present in the PagePermaLink enum.
// Checked before list pages because `activity-details` also contains `activities`.
const DETAIL_PATH_TO_DATA_CLASS: Record<
  string,
  { dataClass: DataClassName; idParam: string }
> = {
  'activity-details': {
    dataClass: DataClassName.ACTIVITY,
    idParam: 'activity_id',
  },
  'company-details': {
    dataClass: DataClassName.COMPANY,
    idParam: 'company_id',
  },
  'contact-details': {
    dataClass: DataClassName.CONTACT,
    idParam: 'contact_id',
  },
  'lead-details': { dataClass: DataClassName.LEAD, idParam: 'lead_id' },
  'opportunity-details': {
    dataClass: DataClassName.OPPORTUNITY,
    idParam: 'opportunity_id',
  },
  'order-details': { dataClass: DataClassName.ORDER, idParam: 'order_id' },
  'person-details': { dataClass: DataClassName.PERSON, idParam: 'person_id' },
  'quote-details': { dataClass: DataClassName.QUOTE, idParam: 'quote_id' },
  'ticket-details': { dataClass: DataClassName.TICKET, idParam: 'ticket_id' },
}

/**
 * Resolves the current page from the CRM URL scheme (detail-page slugs → entity
 * class + id, permalinks → list). Returns null when the URL matches no known
 * page so the core can fall back to dashboard.
 */
export function resolvePageContextFromUrl(): ResolvedPageContext | null {
  const pathname = window.location.pathname
  const detailPathKey = Object.keys(DETAIL_PATH_TO_DATA_CLASS).find((p) =>
    pathname.includes(`/${p}`),
  )
  const detailEntry = detailPathKey
    ? DETAIL_PATH_TO_DATA_CLASS[detailPathKey]
    : undefined
  if (detailEntry) {
    const id = new URLSearchParams(window.location.search).get(
      detailEntry.idParam,
    )
    return { mode: 'detail', dataClass: detailEntry.dataClass, id }
  }

  // Resolve the list/overview page from the pathname. The URL slug matches the
  // page's permalink for every overview page.
  const dataClass = resolveListDataClass(pathname)
  if (dataClass) {
    const urlQuery = Object.fromEntries(
      new URLSearchParams(window.location.search),
    )
    return { mode: 'list', dataClass, id: null, urlQuery }
  }
  return null
}

/** Returns the tab-appropriate start/end dates from URL query params. */
export function getDateRangeFromUrlQuery(
  urlQuery: Record<string, string> | undefined,
): { start: string | undefined; end: string | undefined } {
  if (!urlQuery) return { start: undefined, end: undefined }
  const tab = urlQuery.tab ?? 'my-times'
  if (tab === 'my-team') {
    return { start: urlQuery.teamStart, end: urlQuery.teamEnd }
  }
  if (tab === 'my-orders') {
    return {
      start: urlQuery.ordersStart ?? urlQuery.teamStart,
      end: urlQuery.ordersEnd ?? urlQuery.teamEnd,
    }
  }
  if (tab === 'all-times') {
    return { start: urlQuery.allStart, end: urlQuery.allEnd }
  }
  return { start: urlQuery.start, end: urlQuery.end }
}
